import {joinURL} from "ufo";
import type {ProviderGetImage, ResolvedImageModifiers} from "@nuxt/image";
import {encodeURI} from "js-base64";
import {createOperationsGenerator, defineProvider} from "@nuxt/image/runtime";

const operationsGenerator = createOperationsGenerator({
	joinWith: "/",
	keyMap: {
		resize: "rs",
		size: "s",
		fit: "rt",
		width: "w",
		height: "h",
		dpr: "dpr",
		enlarge: "el",
		extend: "ex",
		gravity: "g",
		crop: "c",
		padding: "pd",
		trim: "t",
		rotate: "rot",
		quality: "q",
		maxBytes: "mb",
		background: "bg",
		backgroundAlpha: "bga",
		blur: "bl",
		sharpen: "sh",
		watermark: "wm",
		preset: "pr",
		cacheBuster: "cb",
		stripMetadata: "sm",
		stripColorProfile: "scp",
		autoRotate: "ar",
		filename: "fn",
		format: "f",
	},
	formatter: (key: string, value: string) => `${key}:${value}`,
});

export const getImage: ProviderGetImage = (
	source: string,
	{modifiers, baseURL},
) => {
	type ImageModifiers = Partial<
		Record<keyof ResolvedImageModifiers | string, string | number>
	>;

	const mergedModifiers: ImageModifiers = Object.fromEntries(
		Object.entries({
			fit: "fill",
			width: 0,
			height: 0,
			gravity: "no",
			enlarge: 1,
			format: "avif",
			...modifiers,
		}).map(([modifier, value]) => [modifier, value?.toString()]),
	);

	const outputFormat = mergedModifiers.format;

	delete mergedModifiers.format;

	const encodedUrl = encodeURI(
		source.startsWith("//") ? `https:${source}` : source,
	);
	const sourcePath = outputFormat
		? `${encodedUrl}.${outputFormat}`
		: encodedUrl;
	const path = joinURL(
		"/insecure",
		operationsGenerator(mergedModifiers),
		sourcePath,
	);

	return {
		url: joinURL(String(baseURL || ""), path),
	};
};

export default defineProvider({
	getImage,
});
